import { useEffect, useRef, useState } from 'react';

type Options<T> = {
  enable?: boolean;          // feature flag
  minItems?: number;         // only apply when items.length >= minItems (default 11)
  itemSelector?: string;     // selector to measure grid items (default '.product-box-container')
  offsetPercent?: number;    // percentage into the list to start duplicating from (default 0.2)
  randomizeSelection?: boolean; // whether to shift picks randomly within pool
};

/**
 * Duplicates items to fill the last row based on the measured number of columns
 * in a wrapped flex/grid container. It appends a few items taken from an offset
 * position (percentage-based) to make duplication less noticeable.
 */
export function useFillLastRowWithDuplicates<T>(
  items: T[],
  options?: Options<T>
) {
  const {
    enable = true,
    minItems = 11, // strictly more than 10
    itemSelector = '.product-box-container',
    offsetPercent = 0.2, // 20%
    randomizeSelection = false,
  } = options || {};

  const containerRef = useRef<HTMLDivElement | null>(null);
  const [displayItems, setDisplayItems] = useState<T[]>(items);
  const [duplicatesApplied, setDuplicatesApplied] = useState(false);

  const computeColumns = () => {
    const root = containerRef.current;
    if (!root) return 0;
    const nodes = Array.from(root.querySelectorAll<HTMLElement>(itemSelector));
    if (nodes.length === 0) return 0;
    const firstTop = nodes[0].offsetTop;
    let count = 0;
    for (const el of nodes) {
      if (el.offsetTop === firstTop) count++;
      else break;
    }
    return Math.max(count, 1);
  };

  const recompute = () => {
    if (!enable || items.length < minItems) {
      setDisplayItems(items);
      setDuplicatesApplied(false);
      return;
    }
    const cols = computeColumns();
    if (!cols) {
      setDisplayItems(items);
      setDuplicatesApplied(false);
      return;
    }
    const remainder = items.length % cols;
    if (remainder === 0) {
      setDisplayItems(items);
      setDuplicatesApplied(false);
      return;
    }
    const missing = cols - remainder;
    const offset = Math.max(3, Math.floor(items.length * offsetPercent));
    const start = Math.min(offset, Math.max(items.length - cols, 0));
    const pool = items.slice(start).concat(items.slice(0, start));
    const maxStartIndex = Math.max(pool.length - missing, 0);
    const startIndex =
      randomizeSelection && maxStartIndex > 0
        ? Math.floor(Math.random() * (maxStartIndex + 1))
        : 0;
    const picks = pool.slice(startIndex, startIndex + missing);
    if (!picks.length) {
      setDisplayItems(items);
      setDuplicatesApplied(false);
      return;
    }
    setDisplayItems([...items, ...picks]);
    setDuplicatesApplied(true);
  };

  useEffect(() => {
    // Wait for the DOM to paint the base list before measuring
    const id = requestAnimationFrame(recompute);
    return () => cancelAnimationFrame(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items, enable, minItems, itemSelector, offsetPercent]);

  useEffect(() => {
    const onResize = () => recompute();
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items, enable, minItems, itemSelector, offsetPercent]);

  useEffect(() => {
    if (!enable) {
      setDuplicatesApplied(false);
    }
  }, [enable]);

  return { containerRef, items: displayItems, duplicatesApplied } as const;
}
